<script setup lang="ts">
import { computed } from 'vue'
/**
 * **行上的内联控件** —— 属性行和输入行共用。
 *
 * 单独抽出来的理由很实在：这两行各写一遍的话，
 * 「控件更新怎么往上抛」「自定义控件插槽怎么转发」迟早只改一处。
 */
import NpWidget from '../widgets/NpWidget.vue'
import type { NodeRow } from '../../types/node'
import type { Widget } from '../../types/widget'

const props = defineProps<{ row: NodeRow }>()

const emit = defineEmits<{
  (e: 'update', widgetId: string, value: unknown): void
  (e: 'resize', widgetId: string, height: number): void
}>()

/**
 * 取控件。
 *
 * 只有属性行 / 输入行有控件。
 *
 * ⚠️ 用 computed 而不是每次在模板里调函数：模板的类型收窄**不跨两次调用**，
 * `v-if="widgetOf(row)"` 挡不住 `:model="widgetOf(row)"` 的类型错误。
 */
const widget = computed<Widget | undefined>(() =>
  props.row.kind === 'property' || props.row.kind === 'input' ? props.row.widget : undefined,
)
</script>

<template>
  <span class="np-row__widget">
    <NpWidget
      v-if="widget"
      :model="widget"
      @update="(id, value) => emit('update', id, value)"
      @resize="(id, height) => emit('resize', id, height)"
    >
      <!-- 自定义控件：原样转发给宿主 -->
      <template #custom="slotProps">
        <slot name="widget-custom" v-bind="slotProps" :row="props.row" />
      </template>
    </NpWidget>
  </span>
</template>
